import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import { OffsiteBackupPanel, DEFAULT_OFFSITE_STRINGS } from '../modules/security';
import { http } from '../utils/apiHttp';

// Ayarlar > Yedekler (yalnızca sistem admini) — madde 15. Sunucudaki yerel yedekler (her gece
// 02:00, son 7) + ortak güvenlik modülünün R2 paneli (OffsiteBackupPanel — ServisPro'dan birebir).
// Geri yükleme ekrandan yapılmaz: CLAUDE.md "Felaket kurtarma" (botlar canlı işlem yapar).
// Backend: /api/admin/backups (backend/src/backup.js).

const BASE = '/api/admin/backups';

const EN_OFFSITE = {
  settingsTitle: 'R2 Settings',
  listTitle: 'Backups in R2',
  hint: 'The nightly backup is also encrypted with AES-256 and sent to Cloudflare R2. Data can be recovered even if the server is lost, the disk fails or ransomware strikes.',
  enabled: 'Send off-site every night',
  accountId: 'Cloudflare Account ID',
  bucket: 'R2 bucket name',
  accessKeyId: 'Access Key ID',
  secret: 'Secret Access Key',
  passphrase: 'Encryption passphrase',
  secretKeep: 'Saved — enter a new value to change it',
  passphraseHint: '⚠️ Store this passphrase OUTSIDE the server (password manager etc.). If the server is lost, backups can only be opened with it. At least 12 characters.',
  keepDays: 'Retention in R2 (days)',
  lastStatus: 'Last upload',
  save: 'Save',
  test: 'Test Connection',
  sendNow: 'Send Latest Backup Now',
  working: 'Working…',
  saved: 'Settings saved.',
  savedWithPassphrase: 'Settings saved. Make sure you stored the encryption passphrase outside the server.',
  failed: 'The operation failed.',
  colFile: 'File',
  colDate: 'Date',
  colSize: 'Size',
  fetch: 'Download to Server',
  fetchConfirm: 'This backup will be downloaded from R2, decrypted and added to the local backup list (it will not be restored yet). Continue?',
  emptyList: 'No backups in R2 yet.',
  show: 'Show',
  hide: 'Hide',
};

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function BackupSettings() {
  const { t, lang } = useTranslation();
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [offsiteMsg, setOffsiteMsg] = useState(null);
  const fmtDate = (d) => new Date(d).toLocaleString(lang === 'en' ? 'en-GB' : 'tr-TR');

  const load = useCallback(() => {
    http.get(BASE).then(({ data }) => setBackups(data)).catch((e) => setMsg({ type: 'error', text: e.message }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const backupNow = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await http.post(`${BASE}/now`);
      setMsg({ type: 'ok', text: t('backup.created', { file: data.fileName }) });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    // Tek sütun, sırayla: 1) Sunucudaki Yedekler 2) R2'deki Yedekler 3) R2 Ayarları — modülün iki
    // sütunlu paneli App.css'teki .ts-backups kurallarıyla tek sütuna alınır (modül dosyası değişmez).
    <div className="ts-backups">
      <p className="settings-note">{t('backup.note')}</p>

      <div className="ofs-card">
      <h3>{t('backup.localTitle')}</h3>
      <div className="settings-input-row">
        <button className="btn btn-start" onClick={backupNow} disabled={busy}>{busy ? '…' : t('backup.now')}</button>
        {msg && <span className={`settings-msg settings-msg-${msg.type}`}>{msg.text}</span>}
      </div>
      <div className="sms-log-table-wrap">
        <table className="sms-log-table">
          <thead><tr><th>{t('backup.colFile')}</th><th>{t('backup.colDate')}</th><th>{t('backup.colSize')}</th><th /></tr></thead>
          <tbody>
            {backups.map((b) => (
              <tr key={b.fileName}>
                <td>{b.fileName}</td>
                <td>{fmtDate(b.createdAt)}</td>
                <td>{fmtSize(b.size)}</td>
                <td><a className="btn" href={`${BASE}/file/${encodeURIComponent(b.fileName)}`}>{t('backup.download')}</a></td>
              </tr>
            ))}
            {backups.length === 0 && <tr><td colSpan={4} className="settings-hint">{t('backup.empty')}</td></tr>}
          </tbody>
        </table>
      </div>
      </div>

      {offsiteMsg && <div className={`auth-alert ${offsiteMsg.type === 'error' ? 'auth-alert-error' : 'auth-alert-success'}`}>{offsiteMsg.text}</div>}
      <OffsiteBackupPanel
        http={http}
        basePath={`${BASE}/offsite`}
        notify={(text, variant) => setOffsiteMsg({ type: variant === 'danger' ? 'error' : 'ok', text })}
        confirm={async (text) => window.confirm(text)}
        onFetched={load}
        errorMessage={(err) => err?.response?.data?.message || err?.message}
        strings={lang === 'en' ? EN_OFFSITE : DEFAULT_OFFSITE_STRINGS}
        buttons={{ primary: 'btn btn-start', secondary: 'btn', row: 'btn' }}
        formatDate={fmtDate}
      />
    </div>
  );
}
